import React, { useState, useEffect, useCallback } from 'react';
import { getAllCollectionsByUserId, addCollection, updateCollectionById } from '../services/apiService';
import './CollectionSelectModal.css';

const CollectionSelectModal = ({ isOpen, onClose, imageId, mode, onCreateCollection }) => {
  // mode: 'create' for creating new collection without image, undefined/null for collection management
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionDescription, setNewCollectionDescription] = useState('');
  const [newCollectionIsPublic, setNewCollectionIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const fetchCollections = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const response = await getAllCollectionsByUserId();
      if (response.success) {
        setCollections(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
      setErrorMessage('Failed to load collections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      // If mode is 'create', directly show the create form
      if (mode === 'create') {
        setShowCreateForm(true);
      } else if (!showCreateForm) {
        fetchCollections();
      }
    } else {
      // Reset form state when modal closes
      setShowCreateForm(false);
      setNewCollectionName('');
      setNewCollectionDescription('');
      setNewCollectionIsPublic(false);
      setErrorMessage('');
      setSuccessMessage('');
    }
  }, [isOpen, mode, showCreateForm, fetchCollections]);


  const handleIndividualSave = async (collectionId) => {
    if (!imageId) {
      setErrorMessage('Invalid image ID');
      return;
    }

    // Show success message first (optimistic update)
    setErrorMessage('');
    setSuccessMessage('Image saved to collection successfully!');

    try {
      // Then show loading
      setSaving(true);

      const collection = collections.find(c => c.id === collectionId);
      const currentImageIds = collection.images || [];
      const currentIds = currentImageIds.map(img => typeof img === 'object' && img.id ? img.id : img);
      
      if (!currentIds.includes(imageId)) {
        const updatedImageIds = [...currentIds, imageId];
        await updateCollectionById(collectionId, { images: updatedImageIds });
        
        // Refresh collections to update UI
        await fetchCollections();
      }

      // Clear success message after a delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 2000);
    } catch (error) {
      console.error('Error saving to collection:', error);
      setSuccessMessage('');
      setErrorMessage('Failed to save image to collection');
    } finally {
      setSaving(false);
    }
  };

  const handleIndividualRemove = async (collectionId) => {
    if (!imageId) {
      setErrorMessage('Invalid image ID');
      return;
    }

    // Show success message first (optimistic update)
    setErrorMessage('');
    setSuccessMessage('Image removed from collection successfully!');

    try {
      // Then show loading
      setSaving(true);

      const collection = collections.find(c => c.id === collectionId);
      const currentImageIds = collection.images || [];
      const currentIds = currentImageIds.map(img => typeof img === 'object' && img.id ? img.id : img);
      
      if (currentIds.includes(imageId)) {
        const updatedImageIds = currentIds.filter(id => id !== imageId);
        await updateCollectionById(collectionId, { images: updatedImageIds });
        
        // Refresh collections to update UI
        await fetchCollections();
      }

      // Clear success message after a delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 2000);
    } catch (error) {
      console.error('Error removing from collection:', error);
      setSuccessMessage('');
      setErrorMessage('Failed to remove image from collection');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCollection = async () => {
    if (!newCollectionName.trim()) {
      setErrorMessage('Collection name is required');
      return;
    }

    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      const collectionData = {
        name: newCollectionName.trim(),
        description: newCollectionDescription.trim() || null,
        isPublic: newCollectionIsPublic,
        images: imageId ? [imageId] : [] // Backend expects array of UUIDs
      };

      if (onCreateCollection) {
        // If onCreateCollection callback is provided, use it (for MyKuraTab create flow)
        await onCreateCollection(collectionData);
        setSuccessMessage('Collection created successfully!');
      } else {
        // Otherwise, use addCollection directly (for image save flow)
        if (!imageId) {
          setErrorMessage('Invalid image ID');
          setSaving(false);
          return;
        }
        await addCollection(collectionData);
        setSuccessMessage('Collection created and image saved successfully!');
      }
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error creating collection:', error);
      setErrorMessage('Failed to create collection');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="collection-select-modal-overlay" onClick={onClose}>
      <div className="collection-select-modal" onClick={(e) => e.stopPropagation()}>
        <button className="collection-select-modal-close" onClick={onClose}>
          ×
        </button>

        {successMessage && (
          <div className="collection-select-success">
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="collection-select-error">
            {errorMessage}
          </div>
        )}

        {!showCreateForm ? (
          <>
            <h2 className="collection-select-modal-title">
              {mode === 'create' ? 'Create Collection' : 'Collection Management'}
            </h2>
            
            <div className="collection-select-list">
              {loading ? (
                <div className="collection-select-loading">Loading collections...</div>
              ) : collections.length === 0 ? (
                <div className="collection-select-empty">
                  No collections yet
                </div>
              ) : (
                collections.map((collection) => {
                  // Check if image already exists in collection
                  const imageExists = imageId && collection.images && 
                    collection.images.map(img => typeof img === 'object' && img.id ? img.id : img).includes(imageId);
                  
                  return (
                    <div
                      key={collection.id}
                      className="collection-select-item"
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="collection-select-item-name">
                          {collection.name}
                        </div>
                        {collection.description && (
                          <div className="collection-select-item-description">{collection.description}</div>
                        )}
                        <div className="collection-select-item-count">
                          {collection.images?.length || 0} image{collection.images?.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                      {imageId && (
                        <div style={{ marginLeft: '0.5rem', display: 'flex', alignItems: 'center' }}>
                          {imageExists ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleIndividualRemove(collection.id);
                              }}
                              disabled={saving}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#ff4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                opacity: saving ? 0.6 : 1,
                                transition: 'all 0.2s ease',
                                width: '80px',
                                minWidth: '80px',
                              }}
                              onMouseEnter={(e) => {
                                if (!saving) {
                                  e.target.style.background = '#ff0000';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!saving) {
                                  e.target.style.background = '#ff4444';
                                }
                              }}
                            >
                              Remove
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleIndividualSave(collection.id);
                              }}
                              disabled={saving}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#82CD59',
                                color: 'black',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                opacity: saving ? 0.6 : 1,
                                transition: 'all 0.2s ease',
                                width: '80px',
                                minWidth: '80px',
                              }}
                              onMouseEnter={(e) => {
                                if (!saving) {
                                  e.target.style.background = '#6db847';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!saving) {
                                  e.target.style.background = '#82CD59';
                                }
                              }}
                            >
                              Save
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {mode !== 'create' && (
              <button
                className="collection-select-create-btn"
                onClick={() => setShowCreateForm(true)}
                disabled={saving}
                style={{ width: '100%', marginTop: '1rem' }}
              >
                + Create Collection
              </button>
            )}
          </>
        ) : (
          <>
            <h2 className="collection-select-modal-title">Create New Collection</h2>
            
            <div className="collection-select-form">
              <div className="collection-select-form-group">
                <label htmlFor="collection-name">Name *</label>
                <input
                  id="collection-name"
                  type="text"
                  value={newCollectionName}
                  onChange={(e) => setNewCollectionName(e.target.value)}
                  placeholder="Enter collection name"
                  disabled={saving}
                />
              </div>

              <div className="collection-select-form-group">
                <label htmlFor="collection-description">Description</label>
                <textarea
                  id="collection-description"
                  value={newCollectionDescription}
                  onChange={(e) => setNewCollectionDescription(e.target.value)}
                  placeholder="Enter collection description (optional)"
                  rows="3"
                  disabled={saving}
                />
              </div>

              <div className="collection-select-form-actions">
                <button
                  className="collection-select-cancel-btn"
                  onClick={() => {
                    // If in create mode, close the modal completely
                    if (mode === 'create') {
                      onClose();
                    } else {
                      setShowCreateForm(false);
                      setNewCollectionName('');
                      setNewCollectionDescription('');
                      setNewCollectionIsPublic(false);
                      setErrorMessage('');
                    }
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="collection-select-save-btn"
                  onClick={handleCreateCollection}
                  disabled={saving || !newCollectionName.trim()}
                >
                  {saving ? 'Creating...' : 'Create Collection'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CollectionSelectModal;

